import { Box, Stack, Typography } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";

/**
 * A number, and what kind of claim it is.
 *
 * ## Why the label carries the basis
 *
 * A row of figures side by side reads as *the same kind of fact*, and on
 * these screens they are not. `$1.05` is arithmetic on usage somebody else
 * counted. `3 runs` is every run this kernel recorded, which is not every run
 * that happened. `1 write` is out of the rows one page read, which is not the
 * log. Three numbers, three completely different warranties, and a card that
 * sets them in a row is quietly claiming they are alike.
 *
 * So the label is not a noun. `cost` is a noun and it is a lie of omission;
 * **`cost · at list`** is the claim. The long form goes in `title`, which is
 * the one click further in — but the short form on screen is already true,
 * because most people will read only that.
 *
 * ## Tabular figures
 *
 * `font-variant-numeric: tabular-nums` on every value, because these stack
 * into columns across cards and proportional digits make a column of numbers
 * look like a column of accidents.
 */
export default function Stat({ value, label, title, tone, mono }) {
  const body = (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="div"
        sx={{
          fontSize: "1.0625rem",
          fontWeight: 600,
          lineHeight: 1.25,
          fontVariantNumeric: "tabular-nums",
          overflowWrap: "anywhere",
          color: tone ? `kernel.${tone}` : "text.primary",
          fontFamily: mono ? "ui-monospace, monospace" : undefined,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{
          // `inline-block` rather than `block` so the dotted rule below is
          // the width of the words and not the width of the column.
          display: "inline-block",
          lineHeight: 1.5,
          // The hint that there is more here. Dotted rather than solid so it
          // does not read as a link to somewhere else — the detail is a
          // sentence about this number, not a page.
          borderBottom: title ? "1px dotted" : undefined,
          borderColor: title ? "divider" : undefined,
          cursor: title ? "help" : undefined,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return title ? <Tooltip title={title}>{body}</Tooltip> : body;
}

/**
 * A row of them.
 *
 * Wraps rather than scrolls, and the gap is wide enough that two stats never
 * read as one phrase. `useFlexGap` is gone in v9; `Stack` always uses `gap`.
 */
export function Stats({ children, sx }) {
  return (
    <Stack
      direction="row"
      sx={{
        flexWrap: "wrap",
        columnGap: 3,
        rowGap: 1.5,
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Stack>
  );
}
